/**
 * What a failure from the chain layer actually means, in words a person can
 * act on.
 *
 * The case this exists for reached a user on the marketplace: an RPC provider
 * was out of quota, answered HTTP 200 with a JSON-RPC error, and ethers turned
 * a body with no result into
 *
 *   missing revert data in call exception; Transaction reverted without a
 *   reason string … "You've reached the usage limit for your current plan"
 *
 * The person was told their transaction had reverted. **Nothing had
 * reverted.** A provider had run out of quota, and the raw ethers dump —
 * complete with the provider's pricing page — went straight to the screen.
 *
 * The gateway now fails over on that body, so this should not arise. This is
 * the second line: if every provider is exhausted at once, the person still
 * gets a sentence rather than a stack trace, and one that does not accuse
 * their transaction of something it did not do.
 */

const text = (error: unknown): string => {
  /*
   * A plain string is the common case and was the one this file first got
   * wrong: the toast provider hands over the joined title and description, not
   * an Error object, so a reader that only looked at `.message` matched
   * nothing at the exact call site it exists for.
   */
  if (typeof error === 'string') return error.toLowerCase()
  const e = error as
    | { message?: unknown; body?: unknown; error?: { message?: unknown }; data?: unknown }
    | null
    | undefined
  return [
    String(e?.message ?? ''),
    String((e as { error?: { message?: unknown } })?.error?.message ?? ''),
    typeof e?.body === 'string' ? e.body : '',
  ]
    .join(' ')
    .toLowerCase()
}

/**
 * The allowance is gone, not merely busy.
 *
 * The gateway says so directly — it is the only party that saw every provider
 * refuse — and the string match is the fallback for a failure that did not
 * come through it.
 */
export function isQuotaExhausted(error: unknown): boolean {
  const t = text(error)
  if (!t.trim()) return false
  if (t.includes('execution reverted')) return false
  return (
    t.includes('rpc_quota_exhausted') ||
    t.includes('daily allowance') ||
    t.includes('usage limit') ||
    t.includes('daily limit') ||
    t.includes('limit for your current plan') ||
    t.includes('please upgrade') ||
    t.includes('out of credits')
  )
}

/** The provider refused to answer: quota, rate limit, capacity, an outage. */
export function isProviderUnavailable(error: unknown): boolean {
  const t = text(error)
  if (!t.trim()) return false
  // A genuine revert carries a reason and must not be mistaken for this.
  if (t.includes('execution reverted')) return false
  return (
    t.includes('rpc_quota_exhausted') ||
    t.includes('rpc_unavailable') ||
    t.includes('usage limit') ||
    t.includes('rate limit') ||
    t.includes('too many requests') ||
    t.includes('limit exceeded') ||
    t.includes('quota') ||
    t.includes('capacity') ||
    t.includes('please upgrade') ||
    t.includes('no backend is currently healthy') ||
    t.includes('server_error') ||
    t.includes('processing response error') ||
    // ethers reports an empty body from a refusing node exactly this way, and
    // it is the shape that was read as a revert.
    (t.includes('missing revert data') && !t.includes('reason='))
  )
}

/**
 * A message for a person, or `undefined` when this is not our case and the
 * caller should keep whatever it already had.
 *
 * Deliberately says what to do rather than what happened: "the network is
 * busy" is information, "try again in a moment" is an instruction.
 */
export function chainReadErrorMessage(error: unknown): string | undefined {
  if (!isProviderUnavailable(error)) return undefined

  /*
   * Two situations, two pieces of advice, and getting them the wrong way round
   * is its own failure. An allowance that resets tomorrow will not clear "in a
   * moment", and somebody retrying every minute until midnight has been sent
   * on an errand by us. A two-second burst told to come back tomorrow is the
   * same mistake pointing the other way.
   *
   * Both say that nothing was sent and nothing was charged, because the
   * message this replaced accused the person's transaction of reverting, and
   * that is the part that makes somebody worry about their money.
   */
  if (isQuotaExhausted(error)) {
    return 'The platform has used up its daily request allowance on the public blockchain nodes. Nothing was sent and nothing was charged — please come back tomorrow to repeat this operation.'
  }
  return 'No blockchain node answered — the network is busy right now. Nothing was sent and nothing was charged. Try again in a moment.'
}
